import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import {
  storeSessionObject,
  storeParticipantForSession,
  clearAllDataForSession, // For clearing a specific session
  storeItems,
  getStoredItems
} from '../utils/sessionStorage';
import { getSessionItems, api } from '../utils/api';

const SessionContext = createContext();

export const useSession = () => useContext(SessionContext);

export const SessionProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [session, setSessionState] = useState(null); // Renamed to avoid conflict in this scope
  const [participant, setParticipantState] = useState(null); // Renamed
  const [participants, setParticipants] = useState([]);
  const [items, setItemsState] = useState([]); // Renamed
  const [activeItem, setActiveItem] = useState(null);
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentVote, setCurrentVoteDirect] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [, setJoinAttempted] = useState(false);
  const [votedCount, setVotedCount] = useState(0);
  const [totalVoters, setTotalVoters] = useState(0);
  const [, setInitializing] = useState(true); // Keep this for initial app load phase

  const activeItemRef = useRef(activeItem);
  useEffect(() => {
    activeItemRef.current = activeItem;
  }, [activeItem]);

  useEffect(() => {
    if (participants && participants.length > 0) {
      const currentTotalVoters = participants.filter(p => p.isVoting !== false && p.isConnected).length;
      setTotalVoters(currentTotalVoters);
    } else {
      setTotalVoters(0);
    }
  }, [participants]);

  // Initial load effect - This is tricky. What should it load?
  // For now, let's assume Session.js will drive loading for its specific session.
  // This effect could be used to load a "last active session ID" if we implement that.
  // Or, it can simply set initializing to false after a brief moment.
  useEffect(() => {
    // console.log("SessionContext: Initializing...");
    // No automatic loading of a global session/participant here anymore.
    // Session.js will be responsible for attempting to load its specific session.
    setInitializing(false); 
  }, []);

  // Store session object when it changes
  useEffect(() => {
    if (session) {
      // console.log('SessionContext: Storing session object due to state change', session);
      storeSessionObject(session);
    }
  }, [session]);

  // Store participant for the current session when participant or session changes
  useEffect(() => {
    if (session && session.sessionId && participant && participant.id) {
      // console.log('SessionContext: Storing participant for session due to state change', session.sessionId, participant);
      storeParticipantForSession(session.sessionId, participant);
    }
  }, [session, participant]);

  // Store items for the current session when items or session changes
  useEffect(() => {
    if (session && session.sessionId && items) {
      // console.log('Storing items in localStorage for session:', session.sessionId, items);
      storeItems(session.sessionId, items);
    }
  }, [session, items]);
  
  // Getter for session state that also handles persistence
  const setSession = (newSession) => {
    setSessionState(newSession);
    // Persistence is handled by the useEffect watching `session` state
  };

  // Getter for participant state
  const setParticipant = (newParticipant) => {
    setParticipantState(newParticipant);
    // Persistence is handled by the useEffect watching `session` and `participant` states
  };
  
  // Getter for items state
  const setItems = (newItems) => {
    setItemsState(newItems);
    // Persistence is handled by the useEffect watching `session` and `items` states
  };

  const getServerUrl = useCallback(() => {
    if (process.env.NODE_ENV === 'production') {
      // Use an environment variable for the production socket URL
      // Fallback to window.location.origin if not set, though it should be set for production.
      return process.env.REACT_APP_SOCKET_URL || window.location.origin;
    }
    
    if (process.env.REACT_APP_DOCKER === 'true') {
      return 'http://localhost:5000';
    }
    
    return 'http://localhost:5000';
  }, []);
  
  const clearSessionContextState = useCallback((sessionIdToClear) => {
    // If a specific sessionId is provided, clear only that session's data
    if (sessionIdToClear) {
      console.log(`SessionContext: Clearing data for specific session ${sessionIdToClear}`);
      clearAllDataForSession(sessionIdToClear);
    } else {
      // If no sessionId, it implies a more global reset or that the current session ID is unknown.
      // For now, let's assume if session.sessionId exists, we clear that.
      // Otherwise, this function might need to be smarter or a different function for total wipe.
      if (session && session.sessionId) {
        console.log(`SessionContext: Clearing data for current session ${session.sessionId} (no specific ID passed to clear)`);
        clearAllDataForSession(session.sessionId);
      } else {
        console.warn("SessionContext: clearSessionContextState called without specific sessionId and no current session in context. No session data cleared from localStorage.");
        // Optionally, could call clearAllPlanningPokerStorage() here for a full wipe if that's the desired fallback.
      }
    }

    // Reset React state
    setSocket(null); // This might be problematic if we want to keep the socket for a new session
    setSessionState(null);
    setParticipantState(null);
    setParticipants([]);
    setItemsState([]);
    setActiveItem(null);
    setVotes([]);
    setLoading(false);
    setError(null);
    setCurrentVoteDirect(null);
    // setIsConnected(false); // Should reflect actual socket state
    setJoinAttempted(false);
    setVotedCount(0);
    setTotalVoters(0);
    // setInitializing(true); // Re-initializing might not be right here.
    console.log('Session context React state reset');
  }, [session]); // Dependency on session to get session.sessionId if no arg passed
  
  const prepareFacilitatorSession = useCallback(async (sessionIdToLoad, facilitatorUserId) => {
    console.log(`SessionContext: Preparing session ${sessionIdToLoad} for facilitator ${facilitatorUserId}`);
    
    if (session && session.sessionId !== sessionIdToLoad) {
      console.log(`SessionContext: Switching from session ${session.sessionId} to ${sessionIdToLoad}, clearing old data`);
      clearSessionContextState(session.sessionId); // Clear specific old session
    } else if (!session) {
      // console.log(`SessionContext: No existing session, ensuring clean state for fresh start`);
      // clearSessionContextState(); // No specific session to clear here if no session exists
    }
    
    setLoading(true);
    setError(null);
    setSessionState(null); 
    setParticipantState(null);
    // setItemsState([]); // Let Session.js handle item loading

    try {
      const response = await api.get(`/sessions/${sessionIdToLoad}`);
      const fetchedSession = response.data;

      if (!fetchedSession || !fetchedSession.participants) {
        console.error('SessionContext: Fetched session is missing or has no participants array.', fetchedSession);
        setError('Failed to load session details: Invalid data received.');
        setLoading(false);
        return false;
      }
      
      // console.log('SessionContext: Fetched session data for facilitator:', fetchedSession);
      storeSessionObject(fetchedSession); // Store the fetched session object

      const facilitatorParticipant = fetchedSession.participants.find(
        p => p.userId === facilitatorUserId && p.role === 'facilitator'
      );

      if (facilitatorParticipant) {
        // console.log('SessionContext: Found facilitator participant:', facilitatorParticipant);
        const preparedParticipant = { 
          ...facilitatorParticipant, 
          isVoting: facilitatorParticipant.isVoting === undefined ? true : facilitatorParticipant.isVoting 
        };

        setSessionState(fetchedSession); // Set React state
        setParticipantState(preparedParticipant); // Set React state
        // Persistence for participant is handled by useEffect watching session & participant

        setLoading(false);
        return true;
      } else {
        console.error(`SessionContext: Facilitator (userId: ${facilitatorUserId}) not found as a participant in session ${sessionIdToLoad}. Participants:`, fetchedSession.participants);
        setError('You are not registered as the facilitator for this session.');
        setLoading(false);
        return false;
      }
    } catch (err) {
      console.error('SessionContext: Error preparing facilitator session:', err);
      setError(err.response?.data?.msg || 'Failed to load session details.');
      setLoading(false);
      return false;
    }
  }, [clearSessionContextState, session]); // Added session dependency for switching logic

  // joinSession now primarily emits to socket; actual state update comes from socket events
  const joinSession = useCallback((sessionId, participantIdToJoin) => { // Renamed participantId arg
    console.log(`SessionContext: Attempting to join session ${sessionId} with participantId ${participantIdToJoin}`);
    setLoading(true); // Indicate loading state
    
    if (!socket) {
      console.error('SessionContext: Cannot join session - Socket not initialized.');
      setError('Connection to server not established.');
      setLoading(false);
      return;
    }
    
    if (!socket.connected) {
      console.warn('SessionContext: Socket not connected, attempting to connect before joining.');
      socket.connect(); // Ensure connection attempt
      // Consider adding a small delay or listening for 'connect' event before emitting 'join-session'
    }

    // It's important that participantIdToJoin is valid
    if (sessionId && participantIdToJoin) {
      console.log(`SessionContext: Emitting 'join-session' for session ${sessionId}, participantId ${participantIdToJoin}`);
      socket.emit('join-session', { sessionId, participantId: participantIdToJoin });
      // setLoading(false) will be handled by socket event handlers like 'session-joined' or 'error'
    } else {
      console.error('SessionContext: Invalid sessionId or participantIdToJoin for joinSession.', { sessionId, participantIdToJoin });
      setError('Cannot join session: Missing session ID or participant ID.');
      setLoading(false);
    }
    // setJoinAttempted(true); // This might be better set inside Session.js or after a successful 'session-joined'
  }, [socket]); // socket is a dependency
  
  useEffect(() => {
    if (session && session.sessionId && participant && (participant.id || participant._id)) {
      // console.log('SessionContext: Socket setup useEffect triggered. Session:', session.sessionId, 'Participant:', participant.id || participant._id); // Commented out
      if (!socket || !socket.connected) {
        // console.log('Attempting to initialize or re-initialize Socket.IO connection...'); // Commented out
        
        if (socket) {
          // console.log('Disconnecting existing socket before creating a new one...'); // Commented out
          socket.disconnect();
        }

        try {
          const serverUrl = getServerUrl();
          const currentSessionId = session.sessionId;
          const currentParticipantId = participant.id || participant._id;

          // console.log(`Connecting to Socket.IO server at: ${serverUrl} for session ${currentSessionId}, participant ${currentParticipantId}`); // Commented out
          
          const newSocket = io(serverUrl, {
            transports: ['websocket', 'polling'],
            timeout: 20000, // Reduced timeout
            forceNew: true,
            reconnection: true,
            reconnectionAttempts: 10, // Reduced attempts
            reconnectionDelay: 2000, // Slightly increased delay
            reconnectionDelayMax: 8000, // Increased max delay
            autoConnect: false,
            query: {
              sessionId: currentSessionId,
              participantId: currentParticipantId,
            }
          });
          
          // console.log('Socket instance created with query:', newSocket.io.opts.query); // Commented out
          
          newSocket.on('connect', () => {
            console.log('Socket connected successfully:', newSocket.id);
            // console.log('Socket transport:', newSocket.io.engine.transport.name); // Commented out
            setIsConnected(true);
            setError(null);
            
            // console.log(`Emitting join-session for ${currentSessionId}, participant ${currentParticipantId} after connect`); // Commented out
            newSocket.emit('join-session', { 
              sessionId: currentSessionId, 
              participantId: currentParticipantId 
            });
            setJoinAttempted(true);
          });
          
          newSocket.on('connect_error', (error) => {
            console.error('Socket connection error:', error.message, error);
            setIsConnected(false);
            setError(`Connection error: ${error.message}`);
          });

          newSocket.io.on('reconnect_attempt', (attempt) => {
            // console.log(`Socket reconnection attempt #${attempt} with query:`, newSocket.io.opts.query); // Commented out
          });

          newSocket.on('reconnect', (attemptNumber) => {
            console.log(`Socket reconnected after ${attemptNumber} attempts`);
            setIsConnected(true);
            setError(null);
            // console.log(`Re-emitting join-session for ${currentSessionId}, p ${currentParticipantId} after reconnect`); // Commented out
            newSocket.emit('join-session', { 
              sessionId: currentSessionId, 
              participantId: currentParticipantId 
            });
          });

          newSocket.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
            setIsConnected(false);
            if (reason === 'io server disconnect') {
              newSocket.connect();
            }
          });

          newSocket.on('session-updated', (data) => {
            console.log('Received session-updated event:', data);
            if (data.session) {
              setSessionState(prevSession => {
                const newSessionState = { ...prevSession, ...data.session };
                // Prevent update if effectively unchanged
                if (prevSession && prevSession.sessionId === newSessionState.sessionId && JSON.stringify(prevSession) === JSON.stringify(newSessionState)) {
                  return prevSession;
                }
                // storeSession call removed from here, will be handled by the new useEffect
                return newSessionState;
              });
            }
            if (data.participant) {
              if (participant && (data.participant.id === participant.id || data.participant._id === participant._id)) {
                setParticipantState(prev => {
                  const newPState = { ...prev, ...data.participant };
                  // Prevent update if effectively unchanged
                  if (prev && (prev.id === newPState.id || prev._id === newPState.id) && JSON.stringify(prev) === JSON.stringify(newPState)) {
                    return prev;
                  }
                  return newPState;
                });
              }
              setParticipants(prevParticipants => 
                prevParticipants.map(p => 
                  (p.id === data.participant.id || p._id === data.participant.id) 
                    ? { ...p, ...data.participant } 
                    : p
                )
              );
            }
          });
    
          newSocket.on('participants-list', (data) => {
            const processedParticipants = data.participants || [];
            setParticipants(processedParticipants);
          });

          newSocket.on('participant-joined', (joinedParticipant) => {
            setParticipants(prevParticipants => {
              const existingIndex = prevParticipants.findIndex(p => (p.id === joinedParticipant.id || p._id === joinedParticipant.id || p.id === joinedParticipant._id));

              if (existingIndex !== -1) {
                const newList = [...prevParticipants];
                newList[existingIndex] = { ...joinedParticipant, isVoting: joinedParticipant.isVoting === undefined ? true : joinedParticipant.isVoting };
                return newList;
              } else {
                const newList = [...prevParticipants, { ...joinedParticipant, isVoting: joinedParticipant.isVoting === undefined ? true : joinedParticipant.isVoting }];
                return newList;
              }
            });
          });

          newSocket.on('participant-updated', (updatedParticipant) => {
            console.log('Socket event: participant-updated', updatedParticipant);
            setParticipants(prev =>
              prev.map(p =>
                (p.id === updatedParticipant.id || p._id === updatedParticipant.id)
                  ? { ...p, ...updatedParticipant, isVoting: updatedParticipant.isVoting === undefined ? true : updatedParticipant.isVoting }
                  : p
              )
            );
            // If the updated participant is the current user, update their context state as well.
            if (participant && (participant.id === updatedParticipant.id || participant._id === updatedParticipant.id)) {
              setParticipantState(prevP => ({ ...prevP, ...updatedParticipant, isVoting: updatedParticipant.isVoting === undefined ? true : updatedParticipant.isVoting }));
            }
          });

          newSocket.on('participant-removed', ({ participantId: removedParticipantId, sessionId: eventSessionId }) => {
            console.log(`Socket event: participant-removed - Participant ID: ${removedParticipantId}, Session ID: ${eventSessionId}`);
            const currentP = participant; // Capture current participant before potential clear due to state update.
            
            setParticipants(prev => prev.filter(p => (p.id !== removedParticipantId && p._id !== removedParticipantId)));
            
            // If the removed participant is the current user, clear their session context.
            if (currentP && (currentP.id === removedParticipantId || currentP._id === removedParticipantId)) {
              console.log(`Current user (participant ID: ${currentP.id || currentP._id}) was removed from the session. Clearing session data.`);
              clearSessionContextState(); // This will trigger navigation/UI changes in components observing the context.
            }
          });
          
          newSocket.on('item-added', (data) => {
            // console.log('Received item-added event:', data); // Commented out for less noise
            if (data.item) {
              const newItem = data.item;
              setItemsState(currentItems => {
                const itemExists = currentItems.some(item => 
                  (item.id && item.id === newItem.id) || 
                  (item._id && item._id === newItem._id)
                );
                if (itemExists) {
                  return currentItems.map(item => 
                    (item.id === newItem.id || item._id === newItem._id)
                      ? { ...item, ...newItem }
                      : item
                  );
                } else {
                  return [...currentItems, newItem];
                }
              });
            }
          });
    
          newSocket.on('items-updated', (data) => {
            // console.log('Received items-updated event:', data); // Commented out for less noise
            if (data.items && Array.isArray(data.items)) {
              // console.log('Updating items list with new data:', data.items); // Also comment out related detailed log if it exists
              setItems(data.items);
              if (session) {
                storeItems(session.sessionId, data.items);
              }
            }
          });
    
          newSocket.on('item-status-updated', (data) => {
            // console.log('Received item-status-updated event:', data); // Commented out for less noise
            if (data.item) {
              setItemsState(currentItems =>
                currentItems.map(item =>
                  item.id === data.item.id ? { ...item, ...data.item } : item
                )
              );
              if (activeItem && activeItem.id === data.item.id) {
                setActiveItem(prevActive => ({ ...prevActive, ...data.item }));
              }
            }
          });
    
          newSocket.on('item-deleted', (data) => {
            console.log('Received item-deleted event:', data);
            if (data.itemId) {
              setItemsState(currentItems =>
                currentItems.filter(item => item.id !== data.itemId)
              );
              if (activeItem && activeItem.id === data.itemId) {
                setActiveItem(null);
              }
            }
          });
    
          newSocket.on('votes-updated', (data) => {
            console.log('Received votes-updated event:', data);
            if (data.itemId && data.votes) {
              if (activeItem && activeItem.id === data.itemId) {
                setVotes(data.votes);
              }
            }
          });
    
          newSocket.on('active-item-set', (data) => {
            console.log('Received active-item-set event:', data);
            
            // Reset hasVoted status for all participants when a new item is set or cleared
            setParticipants(prevParticipants =>
              prevParticipants.map(p => ({ ...p, hasVoted: false }))
            );
            
            if (data.item) {
              setActiveItem(data.item);
              setVotes([]);
              setCurrentVoteDirect(null);
              setVotedCount(0);
              setTotalVoters(data.totalVoters !== undefined ? data.totalVoters : 0);
            } else if (data.itemId === null) {
              setActiveItem(null);
              setVotes([]);
              setCurrentVoteDirect(null);
              setVotedCount(0);
              setTotalVoters(0);
            }
          });
    
          newSocket.on('error', (errorMessage) => {
            console.error('Socket error event received:', errorMessage);
            const msg = (typeof errorMessage === 'string') ? errorMessage : errorMessage.message;
            setError(msg || 'A server error occurred.');
          });
          
          newSocket.on('vote-cast', (data) => {
            console.log('Received vote-cast event:', data);
            const currentActiveItemFromRef = activeItemRef.current; // Use the ref
            console.log('SessionContext: [vote-cast] Current activeItem ID from ref for check:', currentActiveItemFromRef?.id || currentActiveItemFromRef?._id, 'Event itemId:', data.itemId);
            
            if (data.itemId === (currentActiveItemFromRef?.id || currentActiveItemFromRef?._id)) {
              setVotedCount(data.votedCount);
              setTotalVoters(data.totalVoters);
              setParticipants(prevParticipants => 
                prevParticipants.map(p => 
                  ((p.id && p.id === data.participant.id) || (p._id && p._id === data.participant.id))
                    ? { ...p, hasVoted: true } 
                    : p
                )
              );
            } else {
              console.warn('SessionContext: vote-cast event IGNORED for non-active item or mismatched ID. Data itemId:', data.itemId, 'Current Active Item ID from ref:', currentActiveItemFromRef?.id || currentActiveItemFromRef?._id);
            }
          });

          // Optimized vote event handlers
          newSocket.on('vote-count-updated', (data) => {
            console.log('Received vote-count-updated event:', data);
            setVotedCount(data.votedCount);
            setTotalVoters(data.totalVoters);
            
            // Update progress indicators without full participant refresh
            if (data.isComplete) {
              console.log('Voting completed for item:', data.itemId);
            }
          });

          newSocket.on('voting-complete', (data) => {
            console.log('Voting completed for item:', data.itemId);
            // Trigger any completion animations or notifications
            if (data.itemId === activeItem?.id || data.itemId === activeItem?._id) {
              setVotedCount(data.totalVotes || votedCount);
            }
          });

          newSocket.on('all-voted', (data) => {
            console.log('Received all-voted event:', data);
            if (data.itemId === activeItem?.id || data.itemId === activeItem?._id) {
              setVotedCount(data.votedCount);
              setTotalVoters(data.totalVoters);
            }
          });

          newSocket.on('votes-revealed', (data) => {
            console.log('Received votes-revealed event:', data);
            const currentActiveItem = activeItemRef.current; // Use the ref
            console.log('SessionContext: [votes-revealed] currentActiveItem from ref:', currentActiveItem);
            console.log('SessionContext: [votes-revealed] data.itemId:', data.itemId, 'currentActiveItem?.id:', currentActiveItem?.id, 'currentActiveItem?._id:', currentActiveItem?._id);
            
            if (data.itemId && data.votes !== undefined) {
              // Check if the event is for the currently active item using the ref
              if (currentActiveItem && (currentActiveItem.id === data.itemId || currentActiveItem._id === data.itemId)) {
                console.log('SessionContext: [votes-revealed] Item IDs match, updating votes and status');
                setVotes(data.votes);
                setItemsState(currentItems =>
                  currentItems.map(item =>
                    (item.id === data.itemId || item._id === data.itemId) ? { ...item, status: data.itemStatus } : item
                  )
                );
                setActiveItem(prevActive => prevActive ? { ...prevActive, status: data.itemStatus } : null);
              } else {
                console.warn('SessionContext: [votes-revealed] Event for item', data.itemId, 'does not match current active item from ref', currentActiveItem?.id || currentActiveItem?._id, '. Votes/status not updated in UI.');
                // But maybe we should still update the item status in the items list even if it's not active?
                console.log('SessionContext: [votes-revealed] Still updating item status in items list...');
                setItemsState(currentItems =>
                  currentItems.map(item =>
                    (item.id === data.itemId || item._id === data.itemId) ? { ...item, status: data.itemStatus } : item
                  )
                );
              }
            } else {
              console.warn('SessionContext: [votes-revealed] Invalid data received:', data);
            }
          });
          
          newSocket.on('vote-recorded', (data) => {
            console.log('Context: vote-recorded event received, data:', data);
            if (data && data.value !== undefined) { 
              setCurrentVoteDirect(data.value);
            }
          });
          
          newSocket.on('session-data', (data) => {
            console.log('Received session-data event:', data);
            if (data.session) {
              setSessionState(prev => {
                // data.session is the complete new session object
                if (prev && prev.sessionId === data.session.sessionId && JSON.stringify(prev) === JSON.stringify(data.session)) {
                  return prev;
                }
                return data.session;
              });
            }
            if (data.participant) {
              const newPData = { ...data.participant, isVoting: data.participant.isVoting === undefined ? true : data.participant.isVoting };
              setParticipantState(prev => {
                if (prev && (prev.id === newPData.id || prev._id === newPData.id) && JSON.stringify(prev) === JSON.stringify(newPData)) {
                  return prev;
                }
                return newPData;
              });
            }
            if (data.participants) {
              const newParticipantsArray = data.participants.map(p => ({...p, isVoting: p.isVoting === undefined ? true : p.isVoting }));
              setParticipants(prevParticipants => {
                if (JSON.stringify(prevParticipants) === JSON.stringify(newParticipantsArray)) {
                  return prevParticipants;
                }
                return newParticipantsArray;
              });
            }
            if (data.items) {
              setItemsState(prevItems => {
                if (JSON.stringify(prevItems) === JSON.stringify(data.items)) {
                  return prevItems;
                }
                return data.items;
              });
            }
            if (data.activeItem) { // Assuming activeItem can be null
              setActiveItem(prevActiveItem => {
                if (JSON.stringify(prevActiveItem) === JSON.stringify(data.activeItem)) {
                  return prevActiveItem;
                }
                return data.activeItem;
              });
            }
            if (data.currentVote !== undefined) { // currentVote can be null or a string/number
                 setCurrentVoteDirect(prevCurrentVote => {
                    if (prevCurrentVote === data.currentVote) {
                        return prevCurrentVote;
                    }
                    return data.currentVote;
                 });
            }
            // storeSession call removed from here, handled by new useEffect
            setLoading(false);
          });
          
          newSocket.on('estimate-set', (data) => {
            console.log('Received estimate-set event:', data);
            if (data.itemId && data.itemStatus) {
              setItemsState(currentItems =>
                currentItems.map(item => {
                  const itemIdToCompare = item.id || item._id;
                  if (itemIdToCompare === data.itemId) {
                    return { ...item, status: data.itemStatus, finalEstimate: data.finalEstimate };
                  }
                  return item;
                })
              );
              // If this was the active item, clear it to allow selecting the next one.
              const currentActiveItem = activeItemRef.current;
              if (currentActiveItem && (currentActiveItem.id === data.itemId || currentActiveItem._id === data.itemId) && data.itemStatus === 'estimated') {
                console.log('SessionContext: [estimate-set] Item was active and is now estimated, clearing active item.');
                // setActiveItemId(null); // This would call the server again - perhaps better to use context's own method if it handles all client state correctly
                setActiveItem(null); // Clear active item optimistically
                setVotes([]);
                setCurrentVoteDirect(null);
                setVotedCount(0);
                setTotalVoters(0);
                // Optionally, tell server to clear for everyone if not already handled by 'set-estimate' response
                // socket.emit('set-active-item', { itemId: null }); // This might be redundant if server does it. For now, client clear is fine.
              }
            }
          });
          
          setSocket(newSocket);
          newSocket.connect(); // Ensure connection is initiated after all listeners are set up.

        } catch (e) {
          console.error("Failed to create or connect socket:", e);
          setError("Could not connect to the session server.");
          setIsConnected(false);
        }
      } else if (socket && !socket.connected) {
        // console.log('Socket exists but is not connected, attempting to connect...');
        socket.connect();
      }
    } // This closes: if (session && session.sessionId && participant && (participant.id || participant._id))

    // Cleanup function for the useEffect hook
    return () => {
      if (socket) {
        // console.log('Cleaning up socket connection in SessionContext useEffect');
        socket.off('connect');
        socket.off('connect_error');
        socket.io.off('reconnect_attempt'); // Note: socket.io.off for engine events
        socket.off('reconnect');
        socket.off('disconnect');
        
        // Standard event listeners
        socket.off('session-updated');
        socket.off('participants-list');
        socket.off('participant-joined'); // Added for cleanup
        socket.off('participant-updated'); // Added for cleanup
        socket.off('participant-removed'); // Added for cleanup
        socket.off('item-added');
        socket.off('items-updated');
        socket.off('item-status-updated');
        socket.off('item-deleted');
        socket.off('votes-updated');
        socket.off('active-item-set');
        socket.off('vote-cast');
        socket.off('all-voted');
        socket.off('votes-revealed');
        socket.off('error'); // Generic error listener
        
        // Other custom events if any (e.g., 'session-data', if still used)
        socket.off('session-data');

        // Consider whether to disconnect the socket here.
        // If you want the socket to persist across component unmounts/remounts (e.g., for quick re-entry),
        // you might not want to disconnect it here unless it's a full logout/session clear.
        // For now, we are not explicitly calling socket.disconnect() here to allow for reconnections.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, participant?.id, participant?._id, getServerUrl, clearSessionContextState]); // Dependencies for socket effect

  useEffect(() => {
    if (socket && isConnected && session && participant) {
      // socket.emit('participant-heartbeat', { // Temporarily commented out to reduce log noise
      //   sessionId: session.sessionId,
      //   participantId: participant.id || participant._id
      // });
      
      // const heartbeatInterval = setInterval(() => { // Temporarily commented out
      //   if (socket.connected) {
      //     socket.emit('participant-heartbeat', {
      //       sessionId: session.sessionId,
      //       participantId: participant.id || participant._id
      //     });
      //   }
      // }, 2000);
      
      // return () => clearInterval(heartbeatInterval); // Temporarily commented out
    }
  }, [socket, isConnected, session, participant]);

  const updateSession = (newSession) => {
    if (newSession) {
      // If switching to a different session, clear all session-specific data
      if (session && session.sessionId !== newSession.sessionId) {
        console.log(`SessionContext: updateSession - switching from ${session.sessionId} to ${newSession.sessionId}, clearing all session data`);
        setItems([]);
        setActiveItem(null);
        setVotes([]);
        setCurrentVoteDirect(null);
        setVotedCount(0);
        setTotalVoters(0);
        setParticipants([]); // Clear participants when switching sessions
      }
      
      setSession(newSession);
      storeSessionObject(newSession);
    }
  };

  const updateParticipant = (newParticipant) => {
    if (newParticipant) {
      console.log('updateParticipant called with:', newParticipant);
      setParticipant(newParticipant);
      storeParticipantForSession(session.sessionId, newParticipant);
      console.log('Participant updated successfully');
    }
  };

  const updateSessionDescription = useCallback(async (newDescription) => {
    if (!session || !session.sessionId) {
      console.error('Cannot update description: No session in context');
      return false;
    }

    try {
      const response = await api.put(`/sessions/${session.sessionId}`, {
        description: newDescription
      });

      if (response.data) {
        setSessionState(prev => ({
          ...prev,
          description: newDescription
        }));
        console.log('Session description updated successfully');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating session description:', err);
      setError(err.response?.data?.msg || 'Failed to update session description');
      return false;
    }
  }, [session]);

  const updateSessionItems = (newItems) => {
    if (!newItems || !Array.isArray(newItems)) {
      console.error('Invalid items array provided to updateSessionItems');
      return;
    }
    
    console.log('Updating session items:', newItems);
    
    setItems(newItems);
    
    if (session) {
      storeItems(session.sessionId, newItems);
    }
    
    if (socket && socket.connected && session) {
      console.log('Broadcasting updated items to server');
      socket.emit('items-updated', {
        sessionId: session.sessionId,
        items: newItems
      });
    } else {
      console.warn('Socket not connected, items only updated locally');
    }
  };

  const fetchItems = useCallback(() => {
    if (session && session.sessionId) {
      // console.log('Manually fetching items for session:', session.sessionId);
      getSessionItems(session.sessionId)
        .then(itemsData => {
          // console.log('Fetched items manually:', itemsData);
          if (itemsData && itemsData.length > 0) {
            setItems(itemsData);
            if (socket && isConnected) {
              // console.log('Broadcasting fetched items to other participants');
              socket.emit('items-updated', { 
                sessionId: session.sessionId, 
                items: itemsData 
              });
            }
          } else if (itemsData && itemsData.length === 0) { // Handle empty array explicitly
            setItems([]); // Set to empty array
            if (socket && isConnected) {
                // console.log('Broadcasting empty items list');
                socket.emit('items-updated', {
                    sessionId: session.sessionId,
                    items: []
                });
            }
          } else {
            // This case implies itemsData is null or undefined, which might be an error from getSessionItems or an unexpected response.
            // console.warn('fetchItems: itemsData was null or undefined. Not updating items.');
            // Optionally, retrieve from cache if primary fetch fails badly
            const cachedItems = getStoredItems(session.sessionId);
            if (cachedItems && cachedItems.length > 0) {
              // console.log('Using cached items from localStorage due to null/undefined itemsData:', cachedItems);
              setItems(cachedItems);
            }
          }
        })
        .catch(err => {
          console.error('Error fetching items:', err);
          const cachedItems = getStoredItems(session.sessionId);
          if (cachedItems && cachedItems.length > 0) {
            // console.log('Using cached items from localStorage due to fetch error:', cachedItems);
            setItems(cachedItems);
          }
        });
    }
  }, [session, socket, isConnected]);

  const reconnectSocket = (maxAttempts = 3) => {
    if (!socket) {
      console.warn('Cannot reconnect: Socket not initialized');
      setSocket(null);
      return;
    }
    
    let attempts = 0;
    
    const attemptReconnect = () => {
      if (attempts >= maxAttempts) {
        console.error(`Failed to reconnect after ${maxAttempts} attempts`);
        return;
      }
      
      attempts++;
      console.log(`Reconnect attempt ${attempts}/${maxAttempts}`);
      
      if (!socket.connected) {
        socket.connect();
        
        setTimeout(() => {
          if (!socket.connected) {
            attemptReconnect();
          } else {
            console.log('Reconnected successfully');
          }
        }, 1000);
      }
    };
    
    attemptReconnect();
  };

  const debugState = () => {
    console.group('SessionContext State');
    console.log('Socket:', socket?.connected ? 'Connected' : 'Disconnected');
    console.log('IsConnected:', isConnected);
    console.log('Session:', session);
    console.log('Participant:', participant);
    console.log('Participants:', participants.length);
    console.log('Items:', items.length);
    console.log('ActiveItem:', activeItem);
    console.log('Error:', error);
    console.log('Functions available:', {
      setSession: typeof setSession === 'function',
      setParticipant: typeof setParticipant === 'function',
      updateSession: typeof updateSession === 'function',
      updateParticipant: typeof updateParticipant === 'function',
      joinSession: typeof joinSession === 'function'
    });
    console.groupEnd();
  };

  const castVote = (itemId, value) => {
    console.log('SessionContext: castVote triggered with itemId:', itemId, 'value:', value); // Added log
    if (socket && socket.connected) {
      socket.emit('vote', { itemId, value });
    }
  };

  const revealVotes = (itemId) => {
    console.log('SessionContext: revealVotes function called for item:', itemId, 'Socket Connected:', socket?.connected);
    if (!socket) {
      console.error('SessionContext: reveal-votes NOT emitted - socket is null');
      return false;
    }
    if (!socket.connected) {
      console.error('SessionContext: reveal-votes NOT emitted - socket not connected');
      return false;
    }
    if (!itemId) {
      console.error('SessionContext: reveal-votes NOT emitted - itemId is falsy:', itemId);
      return false;
    }
    socket.emit('reveal-votes', { itemId });
    console.log('SessionContext: Emitted reveal-votes for item:', itemId);
    return true;
  };

  const startNextRound = (itemId) => {
    if (socket && socket.connected) {
      socket.emit('next-round', { itemId });
    }
  };

  const setFinalEstimate = (itemId, estimate) => {
    console.log('SessionContext: setFinalEstimate function called for item:', itemId, 'with estimate:', estimate, 'Socket Connected:', socket?.connected); // Added log
    if (socket && socket.connected && itemId && estimate !== undefined && estimate !== null) { // Added null check for estimate
      socket.emit('set-estimate', { itemId, estimate });
      console.log('SessionContext: Emitted set-estimate for item:', itemId, 'estimate:', estimate);
    } else {
      console.warn('SessionContext: set-estimate NOT emitted. Socket:', socket?.connected, 'itemId:', itemId, 'estimate:', estimate);
    }
  };

  const setActiveItemId = (itemId) => {
    console.log(`Setting active item ID: ${itemId}`);
    
    if (!socket || !session) {
      console.warn('Cannot set active item: Socket or session not initialized');
      return;
    }
    
    if (itemId === null) {
      setActiveItem(null);
      setVotes([]);
      setCurrentVoteDirect(null);
      setVotedCount(0);
      setTotalVoters(0);
    }

    if (!socket.connected) {
      console.warn('Socket not connected, attempting to reconnect');
      socket.connect();
    }
    
    try {
      socket.emit('set-active-item', { itemId });
      
      setItems(currentItems => {
        const updatedItems = currentItems.map(item => {
          if (item.status === 'active') {
            return { ...item, status: 'pending' };
          }
          return item;
        });
        
        return updatedItems.map(item => {
          const currentId = item.id || item._id;
          if (currentId === itemId) {
            const updatedItem = { ...item, status: 'active' };
            setActiveItem(updatedItem);
            setVotes([]);
            setCurrentVoteDirect(null);
            setVotedCount(0);
            setTotalVoters(0);
            return updatedItem;
          }
          return item;
        });
      });
    } catch (err) {
      console.error('Error setting active item:', err);
      setError('Failed to set active item. Please try again.');
    }
  };

  const updateParticipantVotingStatus = (isVoting) => {
    if (socket && socket.connected && participant) {
      const participantId = participant.id || participant._id;
      console.log(`Context: Emitting set-participant-voting-status for ${participantId} to ${isVoting}`);
      socket.emit('set-participant-voting-status', { participantId, isVoting });
    }
  };

  const addMultipleItems = useCallback((newItemsArray) => {
    if (!Array.isArray(newItemsArray) || newItemsArray.length === 0) {
      // console.warn('SessionContext: addMultipleItems called with invalid or empty array');
      return;
    }

    let calculatedNewFullList;
    setItemsState(currentItems => {
      const existingItemIds = new Set(currentItems.map(item => item.id || item._id));
      
      const itemsToAdd = newItemsArray
        .map(item => ({ // Ensure new items have a consistent ID structure and default status
          status: 'pending',
          ...item,
          id: item.id || item._id,
          _id: item._id || item.id
        }))
        .filter(item => !existingItemIds.has(item.id || item._id)); // Filter out items that already exist by ID

      calculatedNewFullList = [...currentItems, ...itemsToAdd];
      return calculatedNewFullList;
    });

    setTimeout(() => {
      if (session && session.sessionId && calculatedNewFullList) {
        storeItems(session.sessionId, calculatedNewFullList);
        if (socket && isConnected) {
          socket.emit('items-updated', {
            sessionId: session.sessionId,
            items: calculatedNewFullList
          });
        }
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, socket, isConnected, setItemsState, storeItems]);

  const clearSessionError = useCallback(() => {
    setError(null);
  }, []); // Empty dependency array as setError is stable

  return (
    <SessionContext.Provider
      value={{
        socket,
        session,
        participant,
        participants,
        items,
        activeItem,
        votes,
        loading,
        error,
        currentVote,
        setCurrentVote: setCurrentVoteDirect,
        isConnected,
        votedCount,
        totalVoters,
        joinSession,
        castVote,
        setFinalEstimate,
        setActiveItemId,
        clearSession: clearSessionContextState,
        setSession,
        setParticipant,
        updateSession,
        updateParticipant,
        updateSessionItems,
        debugState,
        revealVotes,
        startNextRound,
        reconnectSocket,
        fetchItems,
        updateParticipantVotingStatus,
        prepareFacilitatorSession,
        addMultipleItems,
        clearSessionError,
        updateSessionDescription
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;